// Use current hostname for API calls (works in both dev and production)
const API_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5002/api'
  : '/api';                       // for production

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  getToken() {
    return this.token || localStorage.getItem('token');
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth
  async login(username, password) {
    const data = await this.request('/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setToken(data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  }

  logout() {
    this.clearToken();
  }

  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Chemical Types
  async getChemicalTypes() {
    return this.request('/chemical-types');
  }

  async addChemicalType(name) {
    return this.request('/chemical-types', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateChemicalType(id, name) {
    return this.request(`/chemical-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteChemicalType(id) {
    return this.request(`/chemical-types/${id}`, {
      method: 'DELETE',
    });
  }

  // Unit Numbers
  async getUnitNumbers() {
    return this.request('/unit-numbers');
  }

  async addUnitNumber(name) {
    return this.request('/unit-numbers', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateUnitNumber(id, name) {
    return this.request(`/unit-numbers/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteUnitNumber(id) {
    return this.request(`/unit-numbers/${id}`, {
      method: 'DELETE',
    });
  }

  // Transactions
  async getTransactions() {
    return this.request('/transactions');
  }

  async addTransaction(transaction) {
    return this.request('/transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async updateTransaction(id, transaction) {
    return this.request(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transaction),
    });
  }

  async deleteTransaction(id) {
    return this.request(`/transactions/${id}`, {
      method: 'DELETE',
    });
  }

  // Stock
  async getStock(chemicalTypeId = null) {
    const queryParam = chemicalTypeId ? `?chemical_type_id=${chemicalTypeId}` : '';
    return this.request(`/stock${queryParam}`);
  }

  // Users
  async getUsers() {
    return this.request('/users');
  }

  async addUser(username, password, role, language) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify({ username, password, role, language }),
    });
  }

  async updateUser(id, username, password, role, language) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ username, password, role, language }),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async unlockUser(username) {
    return this.request(`/users/${encodeURIComponent(username)}/unlock`, {
      method: 'POST',
    });
  }

  async updateUserTimezone(id, timezone) {
    return this.request(`/users/${id}/timezone`, {
      method: 'PUT',
      body: JSON.stringify({ timezone }),
    });
  }

  async updateUserLanguage(id, language) {
    return this.request(`/users/${id}/language`, {
      method: 'PUT',
      body: JSON.stringify({ language }),
    });
  }

  // Stock Notes
  async saveStockNote(chemicalTypeId, notes) {
    return this.request('/stock-notes', {
      method: 'POST',
      body: JSON.stringify({ 
        chemical_type_id: chemicalTypeId, 
        notes 
      }),
    });
  }

  // Order Drafts
  async getOrderDrafts() {
    return this.request('/order-drafts');
  }

  async addOrderDraft(draft) {
    return this.request('/order-drafts', {
      method: 'POST',
      body: JSON.stringify(draft),
    });
  }

  async deleteOrderDraft(id) {
    return this.request(`/order-drafts/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings
  async getSettings() {
    return this.request('/settings');
  }

  async updateSettings(thresholds) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(thresholds),
    });
  }

  // Order Number
  async getOrderNumber() {
    return this.request('/order-number');
  }

  async reserveOrderNumber() {
    return this.request('/order-number/reserve', {
      method: 'POST',
    });
  }

  // Orders
  async getOrders() {
    return this.request('/orders');
  }

  async createOrder(order) {
    return this.request('/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  }

  async confirmOrderHold(id) {
    return this.request(`/orders/${id}/confirm-hold`, {
      method: 'POST',
    });
  }

  async cancelOrderHold(id) {
    return this.request(`/orders/${id}/cancel-hold`, {
      method: 'POST',
    });
  }

  async reopenOrderHold(id, order) {
    return this.request(`/orders/${id}/reopen-hold`, {
      method: 'POST',
      body: JSON.stringify(order),
    });
  }

  async confirmHoldTransaction(id) {
    return this.request(`/transactions/${id}/confirm-hold`, {
      method: 'POST',
    });
  }

  async cancelHoldTransaction(id) {
    return this.request(`/transactions/${id}/cancel-hold`, {
      method: 'POST',
    });
  }
}

export default new ApiService();
